import { DatePipe } from '@angular/common';
import { Component, computed, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router, RouterLink } from '@angular/router';
import {
  AdminButtonComponent,
  AdminConfirmDialogService,
  AdminEmptyStateComponent,
  AdminPageHeaderComponent,
  AdminTableComponent,
  AdminToastService,
} from '../../../shared/admin';
import {
  FEATURE_REQUEST_PRIORITIES,
  FEATURE_REQUEST_STATUSES,
  FEATURE_REQUEST_STATUS_LABELS,
  FEATURE_REQUEST_TYPE_LABELS,
  FEATURE_REQUEST_TYPES,
  type CreateFeatureRequestPayload,
  type FeatureRequestListItem,
  type FeatureRequestStatus,
  type ListFeatureRequestsParams,
} from '../../../shared/models/feature-request.models';
import { FeatureRequestsService } from '../../../shared/services/feature-requests.service';
import { PlatformUsersService, type AssignablePlatformUserDto } from '../../../shared/services/platform-users.service';
import { FeatureRequestCreateModalComponent } from './components/feature-request-create-modal.component';
import { FeatureRequestPriorityBadgeComponent } from './components/feature-request-priority-badge.component';
import { FeatureRequestStatusBadgeComponent } from './components/feature-request-status-badge.component';
import { confirmFeatureRequestStatusChange } from './components/feature-request-status-change.util';
import { AuthService } from '../../../services/auth.service';
import { PermissionService } from '../../permissions/permission.service';

@Component({
  selector: 'app-feature-requests-page',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    RouterLink,
    AdminPageHeaderComponent,
    AdminTableComponent,
    AdminEmptyStateComponent,
    AdminButtonComponent,
    FeatureRequestCreateModalComponent,
    FeatureRequestStatusBadgeComponent,
    FeatureRequestPriorityBadgeComponent,
  ],
  templateUrl: './feature-requests-page.component.html',
  styleUrl: './feature-requests-page.component.scss',
})
export class FeatureRequestsPageComponent implements OnInit, OnDestroy {
  private readonly service = inject(FeatureRequestsService);
  private readonly usersService = inject(PlatformUsersService);
  private readonly auth = inject(AuthService);
  private readonly permissions = inject(PermissionService);
  private readonly toast = inject(AdminToastService);
  private readonly confirmDialog = inject(AdminConfirmDialogService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  private searchTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly createModal = viewChild(FeatureRequestCreateModalComponent);

  readonly showStatusChange = computed(() =>
    this.permissions.canShow('featureRequests', 'statusChange'),
  );
  readonly canStatusChange = computed(() =>
    this.permissions.canExecute('featureRequests', 'statusChange'),
  );
  readonly showAssign = computed(() => this.permissions.canShow('featureRequests', 'assign'));
  readonly canAssign = computed(() => this.permissions.canExecute('featureRequests', 'assign'));
  readonly showDelete = computed(() => this.permissions.canShow('featureRequests', 'delete'));
  readonly canDelete = computed(() => this.permissions.canExecute('featureRequests', 'delete'));
  readonly showAdminControls = computed(
    () => this.showStatusChange() || this.showAssign() || this.showDelete(),
  );
  readonly isAdmin = computed(
    () => this.canStatusChange() || this.canAssign() || this.canDelete(),
  );

  readonly showCreate = computed(() => this.permissions.canShow('featureRequests', 'create'));
  readonly canCreate = computed(() => this.permissions.canExecute('featureRequests', 'create'));

  readonly items = signal<FeatureRequestListItem[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly total = signal(0);
  readonly page = signal(1);
  readonly createOpen = signal(false);
  readonly createSaving = signal(false);
  readonly createError = signal<string | null>(null);

  readonly assignableUsers = signal<AssignablePlatformUserDto[]>([]);

  readonly statuses = FEATURE_REQUEST_STATUSES;
  readonly types = FEATURE_REQUEST_TYPES;
  readonly priorities = FEATURE_REQUEST_PRIORITIES;
  readonly statusLabels = FEATURE_REQUEST_STATUS_LABELS;
  readonly typeLabels = FEATURE_REQUEST_TYPE_LABELS;

  readonly pageSizeOptions = [10, 20, 50];
  pageSize = 20;

  searchInput = '';
  statusFilter = '';
  typeFilter = '';
  priorityFilter = '';
  assignedToFilter = '';
  sortBy = 'createdAt';
  sortDir: 'asc' | 'desc' = 'desc';

  readonly hasNextPage = computed(() => this.page() * this.pageSize < this.total());
  readonly pageSummary = computed(() => {
    const total = this.total();
    if (total === 0) return '0 of 0';
    const start = (this.page() * this.pageSize - this.pageSize) + 1;
    const end = Math.min(this.page() * this.pageSize, total);
    return `${start}–${end} of ${total}`;
  });

  ngOnInit(): void {
    this.usersService.listAssignable().subscribe({
      next: (users) => this.assignableUsers.set(users),
    });

    this.applyRouteParams(this.route.snapshot.queryParamMap);
    this.refreshList();

    this.route.queryParamMap.subscribe((params) => {
      this.applyRouteParams(params);
      this.refreshList();
    });
  }

  ngOnDestroy(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  openCreate(): void {
    this.createError.set(null);
    this.createOpen.set(true);
  }

  closeCreate(): void {
    this.createOpen.set(false);
    this.createModal()?.reset();
  }

  onCreateSubmitted(event: { payload: CreateFeatureRequestPayload; files: File[] }): void {
    this.createSaving.set(true);
    this.createError.set(null);
    const fileCount = event.files.length;
    this.service.create(event.payload).subscribe({
      next: (created) => {
        this.createSaving.set(false);
        this.createOpen.set(false);
        this.createModal()?.reset();
        this.toast.success(
          fileCount > 0
            ? 'Feature request submitted. Uploading attachments…'
            : 'Feature request submitted',
        );
        this.refreshList();

        if (fileCount === 0) {
          return;
        }

        this.service.uploadAttachments(created._id, event.files).subscribe({
          next: () => {
            this.toast.success('Attachments uploaded');
            this.refreshList();
          },
          error: () => {
            this.toast.error('Request saved, but some attachments failed to upload');
            this.refreshList();
          },
        });
      },
      error: () => {
        this.createError.set('Could not create feature request');
        this.createSaving.set(false);
      },
    });
  }

  onSearchChange(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.page.set(1);
      this.syncQueryParams();
      this.refreshList();
    }, 300);
  }

  onFilterChange(): void {
    this.page.set(1);
    this.syncQueryParams();
    this.refreshList();
  }

  resetFilters(): void {
    this.searchInput = '';
    this.statusFilter = '';
    this.typeFilter = '';
    this.priorityFilter = '';
    this.assignedToFilter = '';
    this.sortBy = 'createdAt';
    this.sortDir = 'desc';
    this.page.set(1);
    this.syncQueryParams();
    this.refreshList();
  }

  toggleSort(column: string): void {
    if (this.sortBy === column) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = column;
      this.sortDir = 'asc';
    }
    this.refreshList();
  }

  sortIndicator(column: string): string {
    if (this.sortBy !== column) return '';
    return this.sortDir === 'asc' ? ' ↑' : ' ↓';
  }

  goToDetail(item: FeatureRequestListItem, event: Event): void {
    const target = event.target as HTMLElement;
    if (target.closest('a, button, select, input, textarea')) return;
    void this.router.navigate(['/platform/feature-requests', item._id]);
  }

  async onInlineStatusChange(item: FeatureRequestListItem, newStatus: FeatureRequestStatus): Promise<void> {
    const previous = item.status;

    if (newStatus === previous) return;

    const confirmed = await confirmFeatureRequestStatusChange(
      this.confirmDialog,
      newStatus,
      item.requestNumber,
    );
    if (!confirmed) {
      this.patchItemStatus(item._id, previous);
      return;
    }

    this.patchItemStatus(item._id, newStatus);
    this.service.updateStatus(item._id, newStatus).subscribe({
      next: () => {
        this.toast.success('Status updated');
        this.refreshList();
      },
      error: () => {
        this.patchItemStatus(item._id, previous);
        this.toast.error('Could not update status');
      },
    });
  }

  private patchItemStatus(id: string, status: FeatureRequestStatus): void {
    this.items.update((items) =>
      items.map((row) => (row._id === id ? { ...row, status } : row)),
    );
  }

  prevPage(): void {
    if (this.page() > 1) {
      this.page.update((p) => p - 1);
      this.refreshList();
    }
  }

  nextPage(): void {
    if (this.hasNextPage()) {
      this.page.update((p) => p + 1);
      this.refreshList();
    }
  }

  onPageSizeChange(): void {
    this.page.set(1);
    this.refreshList();
  }

  private refreshList(): void {
    this.loading.set(true);
    this.error.set(null);
    this.service.list(this.buildListParams()).subscribe({
      next: (list) => {
        this.items.set(list.items);
        this.total.set(list.total);
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Could not load feature requests');
        this.loading.set(false);
      },
    });
  }

  private buildListParams() {
    const params: ListFeatureRequestsParams = {
      search: this.searchInput.trim() || undefined,
      type: this.typeFilter || undefined,
      priority: this.priorityFilter || undefined,
      assignedTo: this.assignedToFilter || undefined,
      page: this.page(),
      pageSize: this.pageSize,
      sortBy: this.sortBy,
      sortDir: this.sortDir,
    };

    if (this.statusFilter) {
      params.status = this.statusFilter;
    } else {
      params.excludeStatuses = 'DONE,CLOSED';
    }

    return params;
  }

  private applyRouteParams(params: ParamMap): void {
    if (params.has('status')) this.statusFilter = params.get('status') ?? '';
    if (params.has('search')) this.searchInput = params.get('search') ?? '';
  }

  private syncQueryParams(): void {
    const queryParams: Record<string, string | null> = {
      status: this.statusFilter || null,
      search: this.searchInput.trim() || null,
    };
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
